import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { makeIdsUnique } from '@theme/quick-add-ids';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

/**
 * What every id inside a morphed-in fit-profile panel or size chart row is
 * prefixed with. Any fixed string works; this one reads as "the quick-add
 * copy" in devtools, which is the whole question someone has when they find
 * two panels in one document. See `@theme/quick-add-ids`.
 */
const MORPHED_ID_PREFIX = 'qa-';

/**
 * The blocks quick add brings across from the product page and the host page
 * may also be showing. Both are rendered from the product template, so on a
 * product page a recommendation card's copy arrives with ids identical to the
 * ones already in the document.
 *
 * Scoped to these two rather than the whole fetched grid on purpose. Horizon's
 * own morphed markup — the variant picker, the product form, the media gallery
 * — has always come across as-is, and its ids are read back by `variant-picker`
 * and the Section Rendering API; renaming them is a separate change with its
 * own risks, and not one this addendum needs.
 */
const MORPHED_BLOCK_SELECTOR = '.fit-profile-block, .size-chart-row';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
      ?.href;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#discardCachedProductHtml);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#discardCachedProductHtml);
  }

  /**
   * Throws away the cached product page whenever the cart changes.
   *
   * The cache was harmless while the modal showed only a title, a price and a
   * variant picker — none of those depend on the cart. The fit-profile panel
   * does: Liquid picks `first` or `carried` mode from the cart's own
   * attributes, server-side, at fetch time. So a card opened before a profile
   * existed caches a `first`-mode panel, and re-opening that same card after
   * the customer has saved their measurements would morph that stale panel
   * back in — hidden, because `first` mode hides itself until the buy button
   * asks for it, while `decideSubmitAction` returns `pass` and adds the piece
   * straight away. The customer would see no carry-over sentence and no gift
   * checkbox on the second add from a card they had already opened, which is
   * exactly the hole Addendum 6 exists to close.
   *
   * One refetch per cart change per card, and only on the next open.
   */
  #discardCachedProductHtml = () => {
    this.#cachedProductHtml = null;
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage(this.productPageUrl);
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );

      await this.updateQuickAddModal(freshHtmlCopy);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Store the HTML for later use
      this.#cachedProductHtml = html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // The fetched panel and size chart row both STAY. Quick add asks the same
    // two questions the product page asks (spec Addendum 6, 12.09.2026).
    //
    // Stripping the panel was not merely cosmetic. With a profile already
    // saved, `decideSubmitAction` returns `pass`, so quick add added the piece
    // with no panel shown at all: the carry-over sentence and the gift
    // checkbox never appeared on this path, and a gift bought from a
    // collection page was cut to the buyer's own body with nothing on screen
    // offering a way to say otherwise.
    //
    // Every objection the old code was written against was real, and each is
    // now answered where it belongs rather than by deleting the markup:
    // the modal's allow-list in `quick-add-modal.liquid` names both blocks, so
    // desktop no longer hides them; the mobile branch below re-appends them
    // instead of discarding them with `.product-details`; and the ids are
    // rewritten just below. `panelFor` in theme/assets/fit-profile.js then
    // resolves the morphed panel as this form's OWN — it is inside the
    // quick-add `<dialog>` the form is scoped to, and carries no
    // `data-fit-shared` — while the shared panel stays the fallback it was
    // written to be, for the single-variant card that adds straight from the
    // grid without opening this modal at all.
    // Rebuild both blocks rather than let `morph` patch the ones already in the
    // dialog. Neither carries an `id`, so `morph`'s key check has nothing to
    // compare and it matches them positionally: opening a second card reuses
    // the first card's `<fit-profile>` element and swaps its children
    // underneath it.
    //
    // The element survives that; its wiring does not. `FitProfileElement`
    // caches `heightCm`, `weightKg`, `giftUnknown` and `saveButton` in
    // `connectedCallback`, which does not run again for an element that was
    // never disconnected — and `morph` only calls `updatedCallback()` on
    // Horizon `Component`s, which it is not. Verified in the browser on
    // STAGING: after a second quick add the panel read `data-mode="carried"`
    // while every cached ref pointed at the previous card's detached `first`
    // markup. Ticking the gift box changed nothing on screen, and
    // `lineProperties()` would have stamped the line from inputs that were no
    // longer on the page.
    //
    // Removing first makes each open a teardown and a fresh build: the old
    // element disconnects (settling any gate it held), the new one connects
    // and queries its own markup. Both blocks go, not just the panel — the
    // size chart row re-queries its refs on its own, because it IS a
    // `Component`, but a rule that depends on knowing which components do that
    // is a rule the next person gets wrong.
    for (const stale of modalContent.querySelectorAll(MORPHED_BLOCK_SELECTOR)) stale.remove();

    for (const block of productGrid.querySelectorAll(MORPHED_BLOCK_SELECTOR)) {
      makeIdsUnique(block, MORPHED_ID_PREFIX);
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      // Optional, unlike the four below: a product template need not carry
      // either block, and a sheet without a size chart is worth rendering.
      // Absent means "carry on", not "give up on the whole modal".
      const sizeChartRow = productGrid.querySelector('.size-chart-row');
      const fitProfileBlock = productGrid.querySelector('.fit-profile-block');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      // The product page's order, kept: pick a size, read the chart, give
      // measurements, add to cart. `.product-details` is discarded whole a
      // line below, so anything not re-appended here is gone from the sheet —
      // which is how the panel and the chart went missing on a phone.
      productGrid.appendChild(variantPicker);
      if (sizeChartRow) productGrid.appendChild(sizeChartRow);
      if (fitProfileBlock) productGrid.appendChild(fitProfileBlock);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
